import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Box, Divider, Grid2, IconButton, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import settingsIcon from "../../Assets/Icons/BMSFaults/settingsIcon.svg";
import { PrimaryTooltip } from "../../Components/StyledComponent";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import ScreenContainer from "../../Components/ScreenContainer";
import MultiSelectDropdown from "../../PagesContent/Overview/components/MultiSelectDropdown";
import SeverityStatistics from "../../PagesContent/BmsFaults/SeverityStatistics";
import AlertListItem from "../../PagesContent/BmsFaults/AlertListItem";
import AlertListHeader from "../../PagesContent/BmsFaults/AlertListHeader";
import { Pagination } from "antd";
import CartLoader from "../../Helper/Loaders/CartLoader";
import SelectionClearWarningDialog from "../../PagesContent/BmsFaults/Components/SelectionClearWarningDialog";
import { getPagesMetaData, getConnectedBatteries, getFilterData } from "../../Api/Api";
import { analyticsCalendarEndDate } from "../../Helper/DatePicker/DateConverter";
import OperationalStatusMultiSelect from "../../PagesContent/Common/OperationalStatusMultiSelect";
import AlertSystemTabs from "../../PagesContent/BmsFaults/AlertSystemTabs";
import Events from "../../Analytics/Events";
import CODE from "../../Static/Constants/StatusCodes";
import SingleSelectDropdown from "../../PagesContent/Overview/components/SingleSelectDropdown";
import GlobalFilter from "../../PagesContent/Overview/components/GlobalFilter";
import FilterBar from "../../Components/FilterBar";
import AppCache from "../../Helper/Common/AppCache";
import { getDateRangeFromTimePeriod } from "../../PagesContent/BmsFaults/CommonFunctions";
import useGlobalFilterData from "../../Hooks/useGlobalFilterData";
import {
  getPlatformAlertBatteriesList,
  getPlatformAlertIncidents,
  platformAlertListAction,
  getPlatformAlertStats,
  getPlatformAlertIncidentDetailForAnalyse,
  buildPlatformAlertsCsv,
  normalizeIncident,
} from "../../Api/PlatformAlertsApi";
import SmartAlertsAnalytics from "../../PagesContent/PlatformAlerts/Analytics/SmartAlertsAnalytics";
import PlatformAlertActionDialog from "../../PagesContent/PlatformAlerts/PlatformAlertActionDialog";
import ChartCalendarV2 from "../../PagesContent/FleetPerformance/Components/ChartCalendarV2";
import { isFilterVisible } from "../../Helper/Common/pageFilterVisibility";
import {
  csvColumns,
  csvColumnsForResolved,
  PLATFORM_ALERT_OVERDUE_CONFIG_KEY,
  buildAlertTabStatusFilter,
} from "../../PagesContent/PlatformAlerts/PlatformAlertConfigs";

const tabs = [
  { slug: "active", name: "Active", color: ThemeProperties.purple },
  { slug: "resolved", name: "Resolved", color: ThemeProperties.purple },
  { slug: "analytics", name: "Analytics", color: ThemeProperties.purple },
];

const timePeriodOptions = [
  "Today",
  "Yesterday",
  "Last 7 days",
  "Last 30 days",
  "Last 45 days",
  "Last 90 days",
];

const csvColumnsConfig = { active: csvColumns, resolved: csvColumnsForResolved };

const PlatformAlerts = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [actionDialogData, setActionDialogData] = useState({});
  const [openActionDialog, setOpenActionDialog] = useState(false);
  const handleActionClick = useCallback((battery, alertIncident) => {
    setActionDialogData({ battery, alertIncident });
    setOpenActionDialog(true);
  }, []);
  const queryParams = decryptTheParams();
  const defaultTabSlug = queryParams.tab ? JSON.parse(queryParams.tab) : tabs[0]?.slug;

  const {
    allFleets,
    selectedFleets,
    allOnboardingLocations,
    allOnboardingLocationsHierarchy,
    globalFilters,
    setGlobalFilters,
    fetchFleets,
    fetchOnboardingLocations,
    handleFleetAndLocationChange,
    canUseGlobalAdvancedFilter,
    filtersLoading,
  } = useGlobalFilterData();

  const [pagesContent, setPagesContent] = useState({ mounted: false });
  const [selectedTab, setSelectedTab] = useState(defaultTabSlug);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState(
    defaultTabSlug === "resolved" ? timePeriodOptions[0] : null
  );
  const [allDefaultFiltersApplied, setAllDefaultFiltersApplied] = useState(false);
  const [allGlobalFiltersLoaded, setAllGlobalFiltersLoaded] = useState(false);
  const [isAnalyticsDetailOpen, setIsAnalyticsDetailOpen] = useState(false);

  const [expandAll, setExpandAll] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [isManualSelectAll, setIsManualSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBatteries, setSelectedBatteries] = useState(new Set());
  const [selectedAlertIncidents, setSelectedAlertIncidents] = useState(new Set());
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [batteryList, setBatteryList] = useState([]);
  const [paginationData, setPaginationData] = useState({
    rowsPerPage: 10,
    currentPage: 1,
    total: 1,
  });
  const [searchTerm, setSearchTerm] = useState({
    selectedDevices: [],
    selectedSearchColumn: null,
  });
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedSort, setSelectedSort] = useState([]);
  const [defaultSort, setDefaultSort] = useState([]);

  const incidentsCacheRef = useRef({});
  const fetchRequestRef = useRef(0);

  const globalFilterConfig = AppCache.getItem("globalFilter")
    ?.find((f) => f.type === "unifiedGlobalFilter") || null;

  const overdueThreshold = pagesContent?.metaInfo?.globalConfig?.[PLATFORM_ALERT_OVERDUE_CONFIG_KEY] ?? null;

  // Analytics tab state
  const [analyticsDateRange, setAnalyticsDateRange] = useState(() => {
    const today = new Date();
    const endDate = analyticsCalendarEndDate();
    const startDateObj = new Date();
    startDateObj.setDate(today.getDate() - 7);
    return { startDate: startDateObj.toISOString().split("T")[0], endDate };
  });
  const [vehiclesConnected, setVehiclesConnected] = useState(0);
  const [isLoadingVehiclesCount, setIsLoadingVehiclesCount] = useState(false);
  const [alertCategoryOptions, setAlertCategoryOptions] = useState([]);

  const fetchAlertCategories = async () => {
    const res = await getFilterData({
      type: "dynamic",
      queryParams: { table: "alertMeta", column: "alertType" },
    });
    const list = res?.response?.response?.filterData || [];
    const ids = list.map((c) => c.id);
    setAlertCategoryOptions(ids);
    setGlobalFilters((prev) => ({ ...prev, alertCategories: ids }));
  };

  const fetchVehiclesConnected = useCallback(async () => {
    setIsLoadingVehiclesCount(true);
    try {
      const response = await getConnectedBatteries(globalFilters, "platformAlerts");
      if (response?.responseStatus?.code === CODE.SUCCESS) {
        setVehiclesConnected(response?.response?.data?.[0]?.count || 0);
      }
    } catch {
      setVehiclesConnected(0);
    } finally {
      setIsLoadingVehiclesCount(false);
    }
  }, [globalFilters]);

  const handleGlobalFilterChange = (change) => {
    confirmSelectionClear(() => handleFleetAndLocationChange(change));
  };

  const handleDepotSelection = (selectedOptions) => {
    confirmSelectionClear(() => {
      setGlobalFilters(prev => ({
        ...prev,
        onboardingLocations: { ...prev.onboardingLocations, depots: selectedOptions },
      }));
    });
  };

  const handleOperationalStatusSelection = (selectedValues) => {
    confirmSelectionClear(() => {
      setGlobalFilters(prev => ({ ...prev, operationalStatus: selectedValues }));
    });
  };

  const handleTimePeriodSelection = (selectedOptions) => {
    confirmSelectionClear(() => setSelectedTimePeriod(selectedOptions));
  };

  const handleSearch = (selectedDevices, selectedSearchColumn) => {
    confirmSelectionClear(() => {
      setSearchTerm({ selectedDevices, selectedSearchColumn });
      setPaginationData(prev => ({ ...prev, currentPage: 1 }));
    });
  };

  const handleFilters = (filters) => {
    confirmSelectionClear(() => {
      setSelectedFilters(filters);
      setPaginationData(prev => ({ ...prev, currentPage: 1 }));
    });
  };

  const handleExpandToggle = (updatedValue) => setExpandAll(updatedValue);

  const handleSelectAllToggle = (checked) => {
    setIsManualSelectAll(true);
    setSelectAll(checked);
    if (checked) {
      setSelectedBatteries(new Set(batteryList.map((b) => b.batteryID)));
      // Incidents are fetched lazily on accordion expand — not available here
      setSelectedAlertIncidents(new Set());
    } else {
      setSelectedBatteries(new Set());
      setSelectedAlertIncidents(new Set());
    }
  };

  const handleSelect = useCallback((record, selected) => {
    setSelectedBatteries(prev => {
      const next = new Set(prev);
      if (selected) { next.add(record.batteryID); } else { next.delete(record.batteryID); }
      return next;
    });
  }, []);

  const handleSortChange = (updatedSort) => {
    confirmSelectionClear(() => {
      setSelectedSort([{ sortBy: updatedSort.key, order: updatedSort.order }]);
      setPaginationData(prev => ({ ...prev, currentPage: 1 }));
    });
  };

  const handlePageChange = (page) => {
    confirmSelectionClear(() => setPaginationData(prev => ({ ...prev, currentPage: page })));
  };

  const fetchBatteries = async () => {
    if (!allDefaultFiltersApplied) return;
    const requestId = ++fetchRequestRef.current;
    setIsLoading(true);
    const searchID = searchTerm.selectedSearchColumn || "searchID";
    const dateRange = selectedTab === "resolved" ? getDateRangeFromTimePeriod(selectedTimePeriod) : null;
    const filters = {
      ...(dateRange && dateRange),
      [searchID]: searchTerm.selectedDevices,
      pageCount: paginationData.rowsPerPage,
      pageNum: paginationData.currentPage,
      sortBy: JSON.stringify(selectedSort),
      ...buildAlertTabStatusFilter(selectedTab),
      ...selectedFilters,
    };

    const res = await getPlatformAlertBatteriesList(globalFilters, filters);
    if (requestId !== fetchRequestRef.current) return;

    if (res?.responseStatus?.code !== CODE.SUCCESS) {
      setBatteryList([]);
      setIsLoading(false);
      return;
    }

    setBatteryList(res?.response?.data || []);
    setPaginationData(prev => ({ ...prev, total: res?.response?.total?.total || prev.total }));
    setIsLoading(false);
  };

  // Wraps getPlatformAlertIncidents with field normalization so AlertListItemIncident
  // receives the same field names it uses for BMS Faults (incidentErrorName, bmsAlertStatus, etc.)
  const fetchIncidentsNormalized = useCallback(async (globalFilters, batteryID, filters) => {
    const res = await getPlatformAlertIncidents(globalFilters, batteryID, filters);
    if (res?.response?.data) {
      return { ...res, response: { ...res.response, data: res.response.data.map(normalizeIncident) } };
    }
    return res;
  }, []);

  const incidentFilters = useMemo(() => {
    const dateRange = selectedTab === "resolved" ? getDateRangeFromTimePeriod(selectedTimePeriod) : null;
    const incidentsSortBy = selectedSort.map((sort) =>
      sort.sortBy === "batteriesBySeverity"
        ? { ...sort, sortBy: "severityLevelID" }
        : sort
    );
    return {
      ...buildAlertTabStatusFilter(selectedTab),
      ...(dateRange && dateRange),
      ...selectedFilters,
      sortBy: JSON.stringify(incidentsSortBy),
      skipPagination: true,
    };
  }, [selectedTab, selectedTimePeriod, selectedFilters, selectedSort]);

  const handleIncidentsFetched = useCallback((batteryID, incidents) => {
    incidentsCacheRef.current[batteryID] = incidents;
  }, []);

  const confirmSelectionClear = (action) => {
    if (selectedBatteries.size || selectAll) {
      setPendingAction(() => action);
      setIsWarningOpen(true);
    } else {
      action();
    }
  };

  const clearSelection = () => {
    setSelectedBatteries(new Set());
    setSelectedAlertIncidents(new Set());
    setIsManualSelectAll(false);
    setSelectAll(false);
    setPendingAction(null);
  };

  async function getPageMeta() {
    try {
      const res = await getPagesMetaData("PlatformAlertsV2");
      Events("platformAlertsV2 page opened");
      if (res?.responseStatus?.code === CODE.SUCCESS) {
        setPagesContent({
          metaInfo: res.response?.response?.metaInfo,
          mounted: true,
        });
      } else {
        setPagesContent({ mounted: true });
      }
    } catch {
      setPagesContent({ mounted: true });
    }
  }

  useEffect(() => { getPageMeta(); }, []);
  useEffect(() => { fetchAlertCategories(); }, []);

  useEffect(() => {
    fetchFleets();
  }, []);

  // Per-page filter visibility, the same block BMS Faults and Battery Intelligence
  // read. See isFilterVisible for why an absent key means shown here.
  const pageFiltersConfig = pagesContent?.metaInfo?.globalFilters;
  const showFilter = (key) => isFilterVisible(pageFiltersConfig, key);

  useEffect(() => {
    if (selectedFleets.length > 0) fetchOnboardingLocations(selectedFleets);
  }, [selectedFleets]);

  useEffect(() => {
    // The operational status control is the only thing that seeds this value, so
    // an org that hides it through org meta would wait on something nothing can
    // produce. Wait for it only while it is shown.
    const operationalStatusReady =
      !showFilter("operationalStatus") ||
      (globalFilters?.operationalStatus || []).length > 0;
    const hasAllFilters =
      (globalFilters?.fleets || []).length > 0 &&
      Object.keys(globalFilters?.onboardingLocations || {}).length > 0 &&
      operationalStatusReady;
    setAllGlobalFiltersLoaded(hasAllFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilters, pageFiltersConfig]);

  // The org meta arrives after the first render, so the control mounts, seeds a
  // default, and only then gets hidden. Clear what it seeded: a hidden filter that
  // still scopes the request is one the user cannot see or undo.
  useEffect(() => {
    if (showFilter("operationalStatus")) return;
    setGlobalFilters((prev) =>
      (prev?.operationalStatus || []).length ? { ...prev, operationalStatus: [] } : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFiltersConfig]);

  useEffect(() => {
    const defaultFilters = queryParams.filters ? JSON.parse(queryParams.filters) : {};
    const { originDepo, ...otherFilters } = defaultFilters;
    if (originDepo && Array.isArray(originDepo) && originDepo.length > 0) {
      if (allOnboardingLocations?.depots?.length > 0) {
        setGlobalFilters(prev => ({
          ...prev,
          onboardingLocations: { ...prev.onboardingLocations, depots: originDepo },
        }));
      }
    }
    setAllDefaultFiltersApplied(true);
    if (JSON.stringify(selectedFilters) !== JSON.stringify(otherFilters)) {
      setSelectedFilters(otherFilters);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.filters, allOnboardingLocations]);

  useEffect(() => {
    const defaultPageSort = [{ sortBy: "batteriesBySeverity", order: "desc" }];
    if (queryParams?.sort) {
      setDefaultSort(JSON.parse(queryParams.sort));
      setSelectedSort(JSON.parse(queryParams.sort));
    } else {
      setDefaultSort(defaultPageSort);
      setSelectedSort(defaultPageSort);
    }
  }, [queryParams.sort]);

  useEffect(() => {
    if (allGlobalFiltersLoaded && Object.keys(globalFilters).length > 0) {
      fetchBatteries();
    }
  }, [
    globalFilters,
    paginationData?.currentPage,
    selectedFilters,
    selectedSort,
    searchTerm,
    selectedTimePeriod,
    selectedTab,
    allDefaultFiltersApplied,
    allGlobalFiltersLoaded,
  ]);

  useEffect(() => {
    setSelectedBatteries(new Set());
    setSelectedAlertIncidents(new Set());
  }, [selectedTab]);

  useEffect(() => {
    incidentsCacheRef.current = {};
  }, [globalFilters, incidentFilters]);

  useEffect(() => {
    if (batteryList.length === 0) return;
    const shouldSelectAll = selectedBatteries.size === batteryList.length;
    if (!isManualSelectAll) setSelectAll(shouldSelectAll);
    if (isManualSelectAll) setIsManualSelectAll(false);
  }, [selectedBatteries, batteryList]);

  useEffect(() => {
    if (selectedTab === "analytics" && allGlobalFiltersLoaded && Object.keys(globalFilters).length > 0) {
      fetchVehiclesConnected();
    }
  }, [selectedTab, allGlobalFiltersLoaded, globalFilters, fetchVehiclesConnected]);

  return (
    <ScreenContainer pagesContent={pagesContent}>
      {!isAnalyticsDetailOpen && <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ px: 2, display: "flex" }}>
          <Typography variant="fw700s20lh24c2E2E2E">Smart Alerts</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
            borderBottom: "1px solid #E4E3E3",
            mt: 0,
            pr: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AlertSystemTabs
              tabs={tabs}
              selectedTab={selectedTab}
              changeTheTab={(tabId) => {
                confirmSelectionClear(() => {
                  setSelectedTab(tabId);
                  if (tabId === "resolved" && !selectedTimePeriod) {
                    setSelectedTimePeriod(timePeriodOptions[0]);
                  }
                  setPaginationData(prev => ({ ...prev, currentPage: 1 }));
                });
              }}
            />
          </Box>
          <FilterBar
            testId="smart-alerts-filters"
            filters={[
              selectedTab === "resolved" && showFilter("timePeriod") && {
                key: "timePeriod",
                node: (
                  <SingleSelectDropdown
                    selectedOption={selectedTimePeriod}
                    options={timePeriodOptions}
                    label="Time Period"
                    onChange={handleTimePeriodSelection}
                  />
                ),
              },
              selectedTab === "analytics" && showFilter("dateRange") && {
                key: "dateRange",
                node: (
                  <ChartCalendarV2
                    startDate={analyticsDateRange.startDate}
                    endDate={analyticsDateRange.endDate}
                    onDateChange={({ startDate, endDate }) => setAnalyticsDateRange({ startDate, endDate })}
                    disableAfterDate={analyticsCalendarEndDate()}
                  />
                ),
              },
              selectedTab === "analytics" && showFilter("alertCategories") && {
                key: "alertCategories",
                node: (
                  <MultiSelectDropdown
                    selectedOptions={globalFilters?.alertCategories ?? []}
                    options={alertCategoryOptions}
                    label="Alert Categories"
                    onChange={(selectedOptions) =>
                      setGlobalFilters(prev => ({ ...prev, alertCategories: selectedOptions }))
                    }
                    defaultAllSelected={true}
                  />
                ),
              },
              showFilter("operationalStatus") && {
                key: "operationalStatus",
                node: (
                  <OperationalStatusMultiSelect
                    selectedValues={globalFilters?.operationalStatus || []}
                    onSelectionChange={handleOperationalStatusSelection}
                    onOptionsLoaded={(allValueCodes, defaultSelection) =>
                      setGlobalFilters(prev => ({
                        ...prev,
                        operationalStatus: defaultSelection,
                        allOperationalStatus: allValueCodes,
                      }))
                    }
                  />
                ),
              },
              showFilter("location") && {
                key: "customerLocation",
                primary: true,
                node: canUseGlobalAdvancedFilter && globalFilterConfig ? (
                  <GlobalFilter
                    isLoading={filtersLoading}
                    allFleets={allFleets}
                    allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                    selectedFleetIds={globalFilters?.fleets || []}
                    selectedLocations={globalFilters?.hierarchicalLocation}
                    onChange={handleGlobalFilterChange}
                  />
                ) : (
                  <MultiSelectDropdown
                    selectedOptions={globalFilters?.onboardingLocations?.depots || []}
                    options={allOnboardingLocations?.depots || []}
                    label="Operating Location"
                    onChange={handleDepotSelection}
                  />
                ),
              },
              hasPermission(FEATURE_CODES.PLATFORM_ALERTS_V2_SETTINGS, "view") && {
                key: "alertSettings",
                trailing: true,
                node: (
                  <PrimaryTooltip title="Alert Settings" placement="bottom">
                    <IconButton
                      size="small"
                      onClick={() => navigate("/platform-alerts-v2/alert-settings")}
                      data-testid="alert-settings-btn"
                      sx={{
                        p: "6px",
                        borderRadius: "6px",
                        bgcolor: "#f1f1f1",
                        "&:hover": { bgcolor: "#E0E0E0" },
                      }}
                    >
                      <img src={settingsIcon} alt="Alert Settings" width={25} height={25} />
                    </IconButton>
                  </PrimaryTooltip>
                ),
              },
            ]}
          />
        </Box>
      </Box>}

      <Box sx={{ px: 3, py: 2, mb: 2 }}>
        <Grid2 container gap={2} flexDirection="column">
          {selectedTab === "analytics" ? (
            <SmartAlertsAnalytics
              startDate={analyticsDateRange.startDate}
              endDate={analyticsDateRange.endDate}
              vehiclesConnected={vehiclesConnected}
              isLoadingVehiclesCount={isLoadingVehiclesCount}
              globalFilters={globalFilters}
              allFleets={allFleets}
              allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
              onDetailsViewChange={setIsAnalyticsDetailOpen}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {selectedTab === "active" && (
              <>
                <SeverityStatistics
                  globalFilters={globalFilters}
                  defaultFiltersApplied={allDefaultFiltersApplied}
                  globalFiltersLoaded={allGlobalFiltersLoaded}
                  applyFiltersFromStats={() => {}}
                  fetchStatsFn={getPlatformAlertStats}
                  overdueConfigKey={PLATFORM_ALERT_OVERDUE_CONFIG_KEY}
                />
                <Divider orientation="horizontal" flexItem sx={{ borderColor: "#E4E4E4", mx: "-24px" }} />
              </>
            )}
            <Grid2 container flexDirection="column" gap={2} mb={4}>
              <AlertListHeader
                onExpandToggle={handleExpandToggle}
                onSelectAllToggle={handleSelectAllToggle}
                selectAll={selectAll}
                resultCount={paginationData.total}
                onSearch={handleSearch}
                selectedFilters={selectedFilters}
                onApplyFilters={handleFilters}
                defaultSort={defaultSort}
                onSortChange={handleSortChange}
                selectedTab={selectedTab}
                selectedBatteries={selectedBatteries}
                selectedAlertIncidents={selectedAlertIncidents}
                searchTerm={searchTerm}
                globalFilters={globalFilters}
                selectedTimePeriod={selectedTimePeriod}
                incidentsCache={incidentsCacheRef.current}
                incidentFilters={incidentFilters}
                actionFn={platformAlertListAction}
                fetchIncidentsFn={fetchIncidentsNormalized}
                csvColumnsConfig={csvColumnsConfig}
                filenamePrefix="Smart Alerts"
                buildCsvFn={buildPlatformAlertsCsv}
                filterConfigKey="PlatformAlertsV2Table"
                alertStatusKey="finalStatus"
              />
              {isLoading || !allGlobalFiltersLoaded || !allDefaultFiltersApplied ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5.25, bgcolor: "#F3F5F7" }}>
                  <CartLoader />
                </Box>
              ) : (
                <>
                  {batteryList.length ? (
                    batteryList.map((battery) => (
                      <AlertListItem
                        key={battery.batteryID}
                        battery={battery}
                        expandAll={expandAll}
                        selectedTab={selectedTab}
                        selectedBatteries={selectedBatteries}
                        onSelect={handleSelect}
                        onActionClick={handleActionClick}
                        globalFilters={globalFilters}
                        incidentFilters={incidentFilters}
                        onIncidentsFetched={handleIncidentsFetched}
                        fetchIncidentsFn={fetchIncidentsNormalized}
                        showActions={true}
                        showAnalyse={true}
                        isPlatformAlert={true}
                        analyseInfoFn={getPlatformAlertIncidentDetailForAnalyse}
                        overdueThreshold={overdueThreshold}
                        defaultAlertsSubTab="platform-alerts"
                      />
                    ))
                  ) : (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4, bgcolor: "#F3F5F7" }}>
                      <Typography variant="fw500s12c686868" sx={{ fontWeight: 600, fontSize: "18px", lineHeight: "20px" }}>
                        No Vehicles found for filters applied
                      </Typography>
                    </Box>
                  )}
                  {batteryList.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "right" }}>
                      <Pagination
                        showSizeChanger={false}
                        current={paginationData.currentPage}
                        pageSize={paginationData.rowsPerPage}
                        total={paginationData?.total}
                        onChange={(page) => handlePageChange(page)}
                        showTotal={(total, range) => `${range[0]}-${range[1]} / ${total}`}
                      />
                    </Box>
                  )}
                </>
              )}
            </Grid2>
            <PlatformAlertActionDialog
              actionData={actionDialogData}
              open={openActionDialog}
              onClose={(hasUpdatedData) => {
                setOpenActionDialog(false);
                if (hasUpdatedData) {
                  fetchBatteries();
                }
              }}
            />
            <SelectionClearWarningDialog
              open={isWarningOpen}
              onProceed={() => {
                if (pendingAction) { pendingAction(); setPendingAction(null); }
                setIsWarningOpen(false);
                clearSelection();
              }}
              onCancel={() => { setPendingAction(null); setIsWarningOpen(false); }}
            />
          </Box>
          )}
        </Grid2>
      </Box>
    </ScreenContainer>
  );
};

export default PlatformAlerts;
